import { Form } from 'antd';
import type { FormItemProps } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import type { ReactNode } from 'react';
import { V } from './validators';

/**
 * Compact `<Form.Item>` replacement. See .claude/FORMS.md § What `<Field>` is.
 *
 * The widget is a JSX child so any of the FIELDS.md fields drop in without
 * an adapter. `required` and `optional` are mutually exclusive UX props —
 * they cover 95% of what `rules={[{ required: true }]}` used to do.
 */
interface FieldProps {
  name: NamePath;
  label: ReactNode;
  required?: boolean;
  optional?: boolean;
  rules?: FormItemProps['rules'];
  help?: ReactNode;
  dependencies?: FormItemProps['dependencies'];
  hidden?: boolean;
  valuePropName?: string;
  children: ReactNode;
}

export function Field({
  name,
  label,
  required,
  optional,
  rules,
  help,
  dependencies,
  hidden,
  valuePropName,
  children,
}: FieldProps) {
  const composed: FormItemProps['rules'] = [
    ...(required ? [V.required()] : []),
    ...(rules ?? []),
  ];

  const displayLabel = optional && typeof label === 'string' ? `${label} (optional)` : label;

  return (
    <Form.Item
      name={name}
      label={displayLabel}
      rules={composed}
      help={help}
      dependencies={dependencies}
      hidden={hidden}
      valuePropName={valuePropName}
    >
      {children}
    </Form.Item>
  );
}
